import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ClockIcon,
  MicrophoneIcon,
  PlayIcon,
  SpeakerWaveIcon,
  StopIcon,
} from '@heroicons/react/24/outline';
import { IeltsSpeakingResultAdapter } from './PracticeResultAdapters';
import {
  buildAuthenticIeltsTurnPlan,
  createIeltsRequestId,
  getIeltsUploadProgress,
  IELTS_MIC_START_WINDOW_SECONDS,
  normalizeIeltsAssessment,
  requestIeltsSpeakingFollowup,
  uploadIeltsSpeakingRecording,
} from '../lib/ieltsSpeaking';
import {
  beginAudioSession,
  disposeAudioSession,
  getSessionAudioUrl,
  prefetchSessionAudio,
} from '../lib/sessionAudioCache';
import { playSoundEffect, primeSoundEffects } from '../lib/sfx';

const AI_FOLLOWUP_LIMIT = 2;
const NATURAL_INTERVIEW_PAUSE_MS = 700;

const recorderType = () => [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
].find((type) => window.MediaRecorder?.isTypeSupported?.(type)) || '';

const cueCardSpeech = (turn) => [
  turn.question,
  ...(turn.bullet_points || []).map((point) => `You should say: ${point}`),
].join('. ');

const countdownLabel = (seconds) => `0:${String(Math.max(0, seconds)).padStart(2, '0')}`;

const normalizeDisplayText = (value) => String(value || '')
  .replace(/\s+/g, ' ')
  .trim()
  .toLocaleLowerCase();

const part1TitleContainsQuestion = (title, questions = []) => {
  const normalizedTitle = normalizeDisplayText(title);
  return !!normalizedTitle && questions.some((question) => {
    const normalizedQuestion = normalizeDisplayText(question);
    return !!normalizedQuestion && normalizedTitle.includes(normalizedQuestion);
  });
};

const isLiveFollowupConfig = (config = {}) => (
  Number(config.format_version) >= 2
  && config.simulation_mode === 'part2_3'
  && config.followup_mode === 'adaptive'
);

const generatedQuestions = (turns = []) => turns
  .filter((turn) => turn?.ai_followup_index)
  .sort((left, right) => left.ai_followup_index - right.ai_followup_index)
  .map((turn) => turn.question)
  .filter(Boolean)
  .slice(0, AI_FOLLOWUP_LIMIT);

const playHtmlAudio = (url, audioRef) => new Promise((resolve) => {
  const audio = new Audio(url);
  audioRef.current = audio;
  const finish = () => {
    if (audioRef.current === audio) audioRef.current = null;
    resolve();
  };
  audio.onended = finish;
  audio.onerror = finish;
  try {
    const playback = audio.play();
    playback?.catch?.(finish);
  } catch (_) {
    finish();
  }
});

export default function IeltsAuthenticSimulation({
  config,
  assignmentTitle,
  assignmentId,
  targetId,
  attemptId,
  threadId,
  initialResult,
  onBack,
  t,
}) {
  const initialPlan = useMemo(
    () => buildAuthenticIeltsTurnPlan(config, attemptId || threadId || assignmentId),
    [assignmentId, attemptId, config, threadId],
  );
  const liveFollowups = isLiveFollowupConfig(config);
  const [turns, setTurns] = useState(initialPlan);
  const [phase, setPhase] = useState(initialResult ? 'result' : 'intro');
  const [turnIndex, setTurnIndex] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [micWindowMs, setMicWindowMs] = useState(IELTS_MIC_START_WINDOW_SECONDS * 1000);
  const [answeredTurns, setAnsweredTurns] = useState([]);
  const [missedTurns, setMissedTurns] = useState([]);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(initialResult ? normalizeIeltsAssessment(initialResult) : null);
  const [error, setError] = useState('');
  const [mixingNotice, setMixingNotice] = useState('');
  const [recordingBlob, setRecordingBlob] = useState(null);

  const wholeRecorderRef = useRef(null);
  const wholeChunksRef = useRef([]);
  const recordingModeRef = useRef('pending');
  const audioContextRef = useRef(null);
  const mixDestinationRef = useRef(null);
  const playbackSourceRef = useRef(null);
  const htmlAudioRef = useRef(null);
  const micStreamRef = useRef(null);
  const micSourceRef = useRef(null);
  const micConnectedRef = useRef(false);
  const segmentRecorderRef = useRef(null);
  const segmentStreamRef = useRef(null);
  const segmentChunksRef = useRef([]);
  const segmentResolveRef = useRef(null);
  const turnsRef = useRef(initialPlan);
  const turnIndexRef = useRef(0);
  const latestFixedAnswerRef = useRef(null);
  const pendingFollowupRef = useRef(null);
  const turnStartedAtRef = useRef(0);
  const answerDurationRef = useRef(0);
  const micDeadlineRef = useRef(0);
  const completingRef = useRef(false);
  const finishingAnswerRef = useRef(false);
  const hasAnswerRef = useRef(false);
  const activeRef = useRef(true);
  const phaseRef = useRef(phase);

  const currentTurn = turns[turnIndex] || null;
  const generatedCount = generatedQuestions(turns).length;
  const anticipatedTurnCount = turns.length + (liveFollowups
    ? Math.max(0, AI_FOLLOWUP_LIMIT - generatedCount)
    : 0);

  const changePhase = useCallback((nextPhase) => {
    phaseRef.current = nextPhase;
    setPhase(nextPhase);
  }, []);

  useEffect(() => {
    if (phaseRef.current !== 'intro') return;
    turnsRef.current = initialPlan;
    setTurns(initialPlan);
    turnIndexRef.current = 0;
    setTurnIndex(0);
  }, [initialPlan]);

  const disconnectMicrophone = useCallback(() => {
    if (!micConnectedRef.current) return;
    try {
      micSourceRef.current?.disconnect?.(mixDestinationRef.current);
    } catch (_) {
      try { micSourceRef.current?.disconnect?.(); } catch (__) { /* already disconnected */ }
    }
    micConnectedRef.current = false;
  }, []);

  const releaseMicrophone = useCallback(() => {
    disconnectMicrophone();
    micSourceRef.current = null;
    micStreamRef.current?.getTracks?.().forEach((track) => track.stop());
    micStreamRef.current = null;
  }, [disconnectMicrophone]);

  const closeAudioGraph = useCallback(() => {
    try { playbackSourceRef.current?.stop?.(); } catch (_) { /* source already ended */ }
    playbackSourceRef.current = null;
    try { htmlAudioRef.current?.pause?.(); } catch (_) { /* no active HTML audio */ }
    htmlAudioRef.current = null;
    disconnectMicrophone();
    micSourceRef.current = null;
    mixDestinationRef.current = null;
    const context = audioContextRef.current;
    audioContextRef.current = null;
    if (context && context.state !== 'closed') void context.close?.().catch?.(() => {});
  }, [disconnectMicrophone]);

  const submitBlob = useCallback(async (blob) => {
    if (!blob?.size || !hasAnswerRef.current) {
      setError(t('No answer was recorded. Start the simulation again.'));
      changePhase('empty');
      return;
    }
    setRecordingBlob(blob);
    changePhase('submitting');
    setProgress(0);
    setError('');
    try {
      const actualGenerated = generatedQuestions(turnsRef.current);
      const submissionConfig = liveFollowups
        ? {
          ...config,
          part3_questions: actualGenerated,
          generated_part3_questions: actualGenerated,
          audio_includes_examiner_tts: recordingModeRef.current === 'mixed',
        }
        : {
          ...config,
          audio_includes_examiner_tts: recordingModeRef.current === 'mixed',
        };
      const payload = await uploadIeltsSpeakingRecording({
        blob,
        config: submissionConfig,
        threadId,
        assignmentId,
        targetId,
        attemptId,
        durationMs: answerDurationRef.current,
        stoppedBy: 'authentic_simulation_complete',
        requestId: createIeltsRequestId('authentic-simulation'),
        onUploadProgress: (event) => setProgress((value) => Math.max(value || 0, getIeltsUploadProgress(event))),
      });
      if (!activeRef.current) return;
      const normalized = normalizeIeltsAssessment(payload);
      setProgress(100);
      setResult({
        ...normalized,
        recording_url: normalized.recording_url || URL.createObjectURL(blob),
      });
      changePhase('result');
    } catch (submitError) {
      if (!activeRef.current) return;
      setError(submitError?.message || t('The IELTS speaking request failed.'));
      changePhase('submit_error');
    }
  }, [assignmentId, attemptId, changePhase, config, liveFollowups, targetId, t, threadId]);

  const handleWholeRecorderStop = useCallback(() => {
    const recorder = wholeRecorderRef.current;
    const blob = new Blob(wholeChunksRef.current, { type: recorder?.mimeType || 'audio/webm' });
    wholeRecorderRef.current = null;
    releaseMicrophone();
    closeAudioGraph();
    if (!completingRef.current || !activeRef.current) return;
    completingRef.current = false;
    void submitBlob(blob);
  }, [closeAudioGraph, releaseMicrophone, submitBlob]);

  const configureWholeRecorder = useCallback((recorder) => {
    wholeChunksRef.current = [];
    wholeRecorderRef.current = recorder;
    recorder.ondataavailable = (event) => {
      if (event.data?.size) wholeChunksRef.current.push(event.data);
    };
    recorder.onstop = handleWholeRecorderStop;
  }, [handleWholeRecorderStop]);

  const initializeInterviewRecording = useCallback(async () => {
    if (wholeRecorderRef.current || recordingModeRef.current !== 'pending') return;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!window.MediaRecorder || !AudioContextClass) {
      recordingModeRef.current = 'microphone';
      setMixingNotice(t('This browser cannot mix the examiner voice into the recording. Your microphone answer will still be saved.'));
      return;
    }
    try {
      const context = new AudioContextClass();
      audioContextRef.current = context;
      await context.resume?.();
      const destination = context.createMediaStreamDestination();
      mixDestinationRef.current = destination;
      const type = recorderType();
      const recorder = new window.MediaRecorder(destination.stream, type ? { mimeType: type } : undefined);
      configureWholeRecorder(recorder);
      recorder.start();
      recordingModeRef.current = 'mixed';
    } catch (_) {
      closeAudioGraph();
      wholeRecorderRef.current = null;
      recordingModeRef.current = 'microphone';
      setMixingNotice(t('This browser cannot mix the examiner voice into the recording. Your microphone answer will still be saved.'));
    }
  }, [closeAudioGraph, configureWholeRecorder, t]);

  const ensureMicrophone = useCallback(async () => {
    const existing = micStreamRef.current;
    if (existing?.getAudioTracks?.().some((track) => track.readyState === 'live')) return existing;
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      throw new Error(t('This browser does not support microphone recording.'));
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    micStreamRef.current = stream;
    return stream;
  }, [t]);

  const connectMicrophone = useCallback((stream) => {
    const context = audioContextRef.current;
    const destination = mixDestinationRef.current;
    if (!context || !destination || micConnectedRef.current) return;
    if (!micSourceRef.current) micSourceRef.current = context.createMediaStreamSource(stream);
    micSourceRef.current.connect(destination);
    micConnectedRef.current = true;
  }, []);

  const ensureFallbackRecorder = useCallback((stream) => {
    let recorder = wholeRecorderRef.current;
    if (!recorder) {
      const type = recorderType();
      recorder = new window.MediaRecorder(stream, type ? { mimeType: type } : undefined);
      configureWholeRecorder(recorder);
      recorder.start();
      return;
    }
    if (recorder.state === 'paused') recorder.resume();
  }, [configureWholeRecorder]);

  const startSegmentRecorder = useCallback((stream) => {
    if (!liveFollowups) return;
    let segmentStream = stream;
    try {
      if (typeof stream.clone === 'function') segmentStream = stream.clone();
      const type = recorderType();
      const recorder = new window.MediaRecorder(segmentStream, type ? { mimeType: type } : undefined);
      segmentStreamRef.current = segmentStream === stream ? null : segmentStream;
      segmentChunksRef.current = [];
      segmentRecorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data?.size) segmentChunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(segmentChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        segmentStreamRef.current?.getTracks?.().forEach((track) => track.stop());
        segmentStreamRef.current = null;
        segmentRecorderRef.current = null;
        const resolve = segmentResolveRef.current;
        segmentResolveRef.current = null;
        resolve?.(blob);
      };
      recorder.start();
    } catch (segmentError) {
      segmentStreamRef.current?.getTracks?.().forEach((track) => track.stop());
      segmentStreamRef.current = null;
      segmentRecorderRef.current = null;
      throw segmentError;
    }
  }, [liveFollowups]);

  const stopSegmentRecorder = useCallback(() => {
    const recorder = segmentRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return Promise.resolve(null);
    return new Promise((resolve) => {
      segmentResolveRef.current = resolve;
      try {
        recorder.stop();
      } catch (_) {
        segmentResolveRef.current = null;
        resolve(null);
      }
    });
  }, []);

  const playExaminerTurn = useCallback(async (turn) => {
    const url = await getSessionAudioUrl(cueCardSpeech(turn), 'sentence');
    if (!activeRef.current) return;
    const context = audioContextRef.current;
    const destination = mixDestinationRef.current;
    if (recordingModeRef.current === 'mixed' && context && destination) {
      try {
        await context.resume?.();
        const response = await fetch(url);
        const encoded = await response.arrayBuffer();
        const buffer = await context.decodeAudioData(encoded.slice(0));
        await new Promise((resolve, reject) => {
          const source = context.createBufferSource();
          playbackSourceRef.current = source;
          source.buffer = buffer;
          source.connect(context.destination);
          source.connect(destination);
          source.onended = () => {
            try { source.disconnect(); } catch (_) { /* source already disconnected */ }
            if (playbackSourceRef.current === source) playbackSourceRef.current = null;
            resolve();
          };
          try { source.start(0); } catch (playbackError) { reject(playbackError); }
        });
        return;
      } catch (_) {
        setMixingNotice(t('One examiner prompt could not be mixed into the recording; playback continued.'));
      }
    }
    await playHtmlAudio(url, htmlAudioRef);
  }, [t]);

  useEffect(() => {
    activeRef.current = true;
    beginAudioSession(`ielts-authentic-${attemptId || threadId || assignmentId}`);
    void prefetchSessionAudio(initialPlan.map((turn) => ({ text: cueCardSpeech(turn), mode: 'sentence' })), { concurrency: 2 });
    return () => {
      activeRef.current = false;
      completingRef.current = false;
      finishingAnswerRef.current = false;
      const segmentRecorder = segmentRecorderRef.current;
      if (segmentRecorder && segmentRecorder.state !== 'inactive') {
        try { segmentRecorder.stop(); } catch (_) { /* recorder already stopping */ }
      }
      const recorder = wholeRecorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        try { recorder.stop(); } catch (_) { /* recorder already stopping */ }
      }
      releaseMicrophone();
      closeAudioGraph();
      disposeAudioSession();
    };
  }, [assignmentId, attemptId, closeAudioGraph, initialPlan, releaseMicrophone, threadId]);

  const openMicWindow = useCallback(() => {
    const windowMs = IELTS_MIC_START_WINDOW_SECONDS * 1000;
    micDeadlineRef.current = Date.now() + windowMs;
    setMicWindowMs(windowMs);
    changePhase('mic_window');
    playSoundEffect('micWindowAlert', {
      volume: 1.1,
      throttleMs: 500,
      throttleKey: 'ielts-mic-window-alert',
    });
  }, [changePhase]);

  const beginPreparation = useCallback((seconds) => {
    setSecondsLeft(seconds);
    changePhase('preparation');
  }, [changePhase]);

  const presentTurn = useCallback(async (index) => {
    const turn = turnsRef.current[index];
    if (!turn) return;
    turnIndexRef.current = index;
    setTurnIndex(index);
    changePhase('reading');
    setError('');
    try {
      await playExaminerTurn(turn);
    } catch (_) {
      // Keep the timed interview moving after the playback helper exhausts its fallback.
    }
    if (!activeRef.current) return;
    if (turn.preparation_seconds) beginPreparation(turn.preparation_seconds);
    else openMicWindow();
  }, [beginPreparation, changePhase, openMicWindow, playExaminerTurn]);

  const finishRecording = useCallback(() => {
    const recorder = wholeRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      releaseMicrophone();
      closeAudioGraph();
      setError(t('No answer was recorded. Start the simulation again.'));
      changePhase('empty');
      return;
    }
    completingRef.current = true;
    try {
      recorder.stop();
    } catch (_) {
      completingRef.current = false;
      setError(t('No answer was recorded. Start the simulation again.'));
      changePhase('empty');
    }
  }, [changePhase, closeAudioGraph, releaseMicrophone, t]);

  const scheduleTurn = useCallback((index, delay = NATURAL_INTERVIEW_PAUSE_MS) => {
    changePhase('transition');
    window.setTimeout(() => {
      if (activeRef.current) void presentTurn(index);
    }, delay);
  }, [changePhase, presentTurn]);

  const requestFollowup = useCallback(async (answer) => {
    if (!answer?.blob?.size || !answer?.turn) {
      finishRecording();
      return;
    }
    const pending = answer.requestId
      ? answer
      : { ...answer, requestId: createIeltsRequestId(`followup-${answer.turn.id}`) };
    pendingFollowupRef.current = pending;
    const wholeRecorder = wholeRecorderRef.current;
    if (recordingModeRef.current === 'mixed' && wholeRecorder?.state === 'recording') {
      try { wholeRecorder.pause(); } catch (_) { /* keep recording if pause is unsupported */ }
    }
    changePhase('generating_followup');
    setError('');
    try {
      const askedQuestions = generatedQuestions(turnsRef.current);
      const payload = await requestIeltsSpeakingFollowup({
        blob: pending.blob,
        attemptId,
        threadId,
        turnId: pending.turn.id,
        question: pending.turn.question,
        part: pending.turn.part,
        cueCard: config.part2_cue_card || {},
        askedQuestions,
        requestId: pending.requestId,
      });
      if (!activeRef.current) return;
      const question = String(
        payload?.question
        || payload?.followup_question
        || payload?.result?.question
        || '',
      ).trim();
      if (!question) throw new Error(t('The AI follow-up is temporarily unavailable. Retry this question.'));
      const responseIndex = Number(payload?.question_index);
      const nextFollowupIndex = Number.isFinite(responseIndex) && responseIndex >= 1
        ? Math.min(AI_FOLLOWUP_LIMIT, Math.round(responseIndex))
        : Math.min(AI_FOLLOWUP_LIMIT, askedQuestions.length + 1);
      const id = `part3-ai-${nextFollowupIndex}`;
      let nextTurns = turnsRef.current;
      let nextIndex = nextTurns.findIndex((turn) => turn.id === id);
      if (nextIndex < 0) {
        nextTurns = [...nextTurns, {
          id,
          part: '3',
          question,
          preparation_seconds: 0,
          answer_limit_seconds: 60,
          ai_followup_index: nextFollowupIndex,
        }];
        nextIndex = nextTurns.length - 1;
        turnsRef.current = nextTurns;
        setTurns(nextTurns);
      }
      pendingFollowupRef.current = null;
      if (wholeRecorder?.state === 'paused') {
        try { wholeRecorder.resume(); } catch (_) { /* continue with the available recording */ }
      }
      scheduleTurn(nextIndex);
    } catch (followupError) {
      if (!activeRef.current) return;
      setError(followupError?.message || t('The AI follow-up is temporarily unavailable. Retry this question.'));
      changePhase('followup_error');
    }
  }, [attemptId, changePhase, config.part2_cue_card, finishRecording, scheduleTurn, t, threadId]);

  const advanceMissedTurn = useCallback((turn, index) => {
    if (turn?.id) setMissedTurns((items) => (items.includes(turn.id) ? items : [...items, turn.id]));
    changePhase('missed');
    window.setTimeout(() => {
      if (!activeRef.current) return;
      const nextIndex = index + 1;
      if (turnsRef.current[nextIndex]) {
        scheduleTurn(nextIndex, 0);
        return;
      }
      if (liveFollowups && !turn?.ai_followup_index) {
        const latest = latestFixedAnswerRef.current;
        if (latest?.blob?.size && generatedQuestions(turnsRef.current).length < AI_FOLLOWUP_LIMIT) {
          void requestFollowup(latest);
          return;
        }
      }
      finishRecording();
    }, 800);
  }, [changePhase, finishRecording, liveFollowups, requestFollowup, scheduleTurn]);

  const finishCurrentAnswer = useCallback(async () => {
    if (finishingAnswerRef.current || phaseRef.current !== 'answering') return;
    finishingAnswerRef.current = true;
    changePhase('transition');
    const index = turnIndexRef.current;
    const turn = turnsRef.current[index];
    answerDurationRef.current += Math.max(0, Date.now() - turnStartedAtRef.current);
    hasAnswerRef.current = true;
    if (turn?.id) setAnsweredTurns((items) => (items.includes(turn.id) ? items : [...items, turn.id]));
    disconnectMicrophone();
    const wholeRecorder = wholeRecorderRef.current;
    if (recordingModeRef.current === 'microphone' && wholeRecorder?.state === 'recording') {
      try { wholeRecorder.pause(); } catch (_) { /* keep the one recording alive */ }
    }
    const segmentBlob = await stopSegmentRecorder();
    const currentAnswer = segmentBlob?.size ? { blob: segmentBlob, turn } : null;
    if (currentAnswer && !turn?.ai_followup_index) latestFixedAnswerRef.current = currentAnswer;
    finishingAnswerRef.current = false;
    if (!activeRef.current) return;

    const nextIndex = index + 1;
    if (turnsRef.current[nextIndex]) {
      scheduleTurn(nextIndex);
      return;
    }
    if (liveFollowups && generatedQuestions(turnsRef.current).length < AI_FOLLOWUP_LIMIT) {
      const source = turn?.ai_followup_index ? currentAnswer : (currentAnswer || latestFixedAnswerRef.current);
      if (source?.blob?.size) {
        void requestFollowup(source);
        return;
      }
    }
    finishRecording();
  }, [changePhase, disconnectMicrophone, finishRecording, liveFollowups, requestFollowup, scheduleTurn, stopSegmentRecorder]);

  useEffect(() => {
    if (phase !== 'preparation') return undefined;
    const timer = window.setInterval(() => {
      setSecondsLeft((value) => {
        if (value <= 1) {
          window.clearInterval(timer);
          window.setTimeout(openMicWindow, 0);
          return 0;
        }
        return value - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [openMicWindow, phase]);

  useEffect(() => {
    if (phase !== 'mic_window') return undefined;
    const timer = window.setInterval(() => {
      const remaining = Math.max(0, micDeadlineRef.current - Date.now());
      setMicWindowMs(remaining);
      if (!remaining) {
        window.clearInterval(timer);
        const index = turnIndexRef.current;
        advanceMissedTurn(turnsRef.current[index], index);
      }
    }, 50);
    return () => window.clearInterval(timer);
  }, [advanceMissedTurn, phase]);

  useEffect(() => {
    if (phase !== 'answering') return undefined;
    const timer = window.setInterval(() => {
      const turn = turnsRef.current[turnIndexRef.current];
      const elapsed = Date.now() - turnStartedAtRef.current;
      const remaining = Math.max(0, (turn?.answer_limit_seconds || 60) - Math.floor(elapsed / 1000));
      setSecondsLeft(remaining);
      if (!remaining) {
        window.clearInterval(timer);
        void finishCurrentAnswer();
      }
    }, 100);
    return () => window.clearInterval(timer);
  }, [finishCurrentAnswer, phase]);

  const beginSimulation = async () => {
    primeSoundEffects();
    changePhase('starting');
    setError('');
    await initializeInterviewRecording();
    if (activeRef.current) await presentTurn(0);
  };

  const turnOnMicrophone = async () => {
    if (Date.now() > micDeadlineRef.current || phaseRef.current !== 'mic_window') return;
    changePhase('requesting');
    setError('');
    try {
      const stream = await ensureMicrophone();
      startSegmentRecorder(stream);
      if (recordingModeRef.current === 'mixed') connectMicrophone(stream);
      else ensureFallbackRecorder(stream);
      turnStartedAtRef.current = Date.now();
      setSecondsLeft(turnsRef.current[turnIndexRef.current]?.answer_limit_seconds || 60);
      changePhase('answering');
    } catch (micError) {
      disconnectMicrophone();
      await stopSegmentRecorder();
      const recorder = wholeRecorderRef.current;
      if (recordingModeRef.current === 'microphone' && recorder?.state === 'recording') {
        try { recorder.pause(); } catch (_) { /* recorder remains usable */ }
      }
      setError(micError?.message || t('Microphone access was not granted.'));
      changePhase('mic_error');
    }
  };

  const retryFollowup = () => {
    if (pendingFollowupRef.current) void requestFollowup(pendingFollowupRef.current);
  };

  if (phase === 'result' && result) {
    return (
      <main className="mx-auto max-w-4xl p-4 pb-16 md:p-8">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-700 hover:underline"><ArrowLeftIcon className="h-4 w-4" />{t('Back to My Practices')}</button>
        <section className="mt-6 border border-emerald-200 bg-emerald-50 p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">{t('Authentic simulation complete')}</div>
          <h1 className="mt-1 text-2xl font-bold text-gray-950">{t('Your IELTS speaking result')}</h1>
          <p className="mt-2 text-sm text-gray-700">{t('{answered} answered, {missed} missed microphone windows.', { answered: answeredTurns.length, missed: missedTurns.length })}</p>
        </section>
        <div className="mt-6"><IeltsSpeakingResultAdapter attempt={{ ...result, practice_type: 'ielts_speaking' }} details={result} t={t} /></div>
      </main>
    );
  }

  const modeLabel = config.simulation_mode === 'custom'
    ? t('Custom practice')
    : config.simulation_mode === 'part1'
      ? t('Part 1')
      : t('Part 2 + Part 3');
  const visibleAssignmentTitle = config.simulation_mode === 'part1'
    && part1TitleContainsQuestion(assignmentTitle, config.part1_questions)
    ? t('Part 1 interview')
    : assignmentTitle || t('IELTS Speaking Practice');

  return (
    <main className="mx-auto max-w-3xl p-4 pb-16 md:p-8">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-700 hover:underline"><ArrowLeftIcon className="h-4 w-4" />{t('Back to My Practices')}</button>
      <section className="mt-6 border border-gray-200 bg-white p-5 sm:p-7">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-violet-700">{t('Authentic IELTS speaking simulation')}</div>
            <h1 className="mt-1 text-2xl font-bold text-gray-950" data-i18n-skip>{visibleAssignmentTitle}</h1>
          </div>
          <span className="rounded-full bg-violet-100 px-3 py-1 text-sm font-semibold text-violet-900">{modeLabel}</span>
        </div>

        {phase === 'intro' && (
          <div className="mt-6">
            <p className="leading-7 text-gray-700">{t('Questions are read aloud one at a time. The examiner voice, natural pauses, and your answers are kept together in one interview recording. After each question, you have ten seconds to turn on the microphone.')}</p>
            <ul className="mt-4 space-y-2 text-sm text-gray-600">
              <li>{t('{count} examiner turns in this attempt.', { count: anticipatedTurnCount })}</li>
              {liveFollowups && <li>{t('Two Part 3 questions will be generated live from your answers.')}</li>}
              {config.simulation_mode === 'part2_3' && <li>{t('Part 2 includes one minute of preparation and up to two minutes to speak.')}</li>}
            </ul>
            <button type="button" onClick={() => { void beginSimulation(); }} disabled={!turns.length} className="mt-6 inline-flex items-center gap-2 rounded bg-violet-700 px-5 py-3 font-semibold text-white hover:bg-violet-800 disabled:opacity-50"><PlayIcon className="h-5 w-5" />{t('Begin simulation')}</button>
          </div>
        )}

        {phase === 'starting' && <div className="mt-6 inline-flex items-center gap-2 font-semibold text-violet-800"><ArrowPathIcon className="h-5 w-5 animate-spin" />{t('Preparing the interview recording...')}</div>}

        {phase !== 'intro' && !['starting', 'submitting', 'submit_error', 'empty'].includes(phase) && currentTurn && (
          <div className="mt-6">
            <div className="flex items-center justify-between gap-3 text-sm text-gray-500"><span>{t('Question {current} of {total}', { current: turnIndex + 1, total: anticipatedTurnCount })}</span><span className="font-semibold text-violet-800">{currentTurn.part ? t('Part {part}', { part: currentTurn.part }) : t('Custom practice')}</span></div>
            {String(currentTurn.part) !== '1' && (
              <div className="mt-3 border-l-4 border-violet-500 bg-violet-50 px-4 py-4">
                <p className="whitespace-pre-wrap text-lg font-semibold leading-7 text-gray-950" data-i18n-skip>{currentTurn.question}</p>
                {!!currentTurn.bullet_points?.length && <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-gray-700">{currentTurn.bullet_points.map((point) => <li key={point} data-i18n-skip>{point}</li>)}</ul>}
              </div>
            )}
            {phase === 'reading' && <div className="mt-5 inline-flex items-center gap-2 font-semibold text-violet-800"><SpeakerWaveIcon className="h-5 w-5 animate-pulse" />{t('Examiner is reading the question...')}</div>}
            {phase === 'preparation' && <div className="mt-5 rounded border border-amber-200 bg-amber-50 p-4"><div className="flex items-center justify-between"><span className="font-semibold text-amber-900">{t('Preparation time')}</span><span className="font-mono text-2xl font-bold text-amber-900">{countdownLabel(secondsLeft)}</span></div><p className="mt-2 text-sm text-amber-900">{t('Use this minute to make brief notes. Your answer microphone is not active yet.')}</p></div>}
            {phase === 'mic_window' && <div className="mt-5 rounded border border-rose-200 bg-rose-50 p-4"><div className="flex items-center justify-between gap-3"><div><div className="font-semibold text-rose-900">{t('Turn on the microphone now')}</div><div className="mt-1 text-sm text-rose-800">{t('The reminder sound has played. Click within ten seconds.')}</div></div><span className="font-mono text-2xl font-bold text-rose-900">{(micWindowMs / 1000).toFixed(1)}</span></div><button type="button" onClick={turnOnMicrophone} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded bg-rose-600 px-5 py-3 font-bold text-white hover:bg-rose-700"><MicrophoneIcon className="h-6 w-6" />{t('Turn on microphone')}</button></div>}
            {phase === 'requesting' && <div className="mt-5 inline-flex items-center gap-2 font-semibold text-violet-800"><ArrowPathIcon className="h-5 w-5 animate-spin" />{t('Requesting microphone access...')}</div>}
            {phase === 'answering' && <div className="mt-5 rounded border border-emerald-200 bg-emerald-50 p-4"><div className="flex items-center justify-between"><span className="inline-flex items-center gap-2 font-semibold text-emerald-900"><MicrophoneIcon className="h-5 w-5" />{t('Recording')}</span><span className="font-mono text-2xl font-bold text-emerald-900">{countdownLabel(secondsLeft)}</span></div><button type="button" onClick={() => { void finishCurrentAnswer(); }} className="mt-4 inline-flex items-center gap-2 rounded bg-gray-900 px-5 py-2.5 font-semibold text-white hover:bg-black"><StopIcon className="h-5 w-5" />{t('Finish this answer')}</button></div>}
            {phase === 'missed' && <div className="mt-5 rounded border border-rose-200 bg-rose-50 p-4 font-semibold text-rose-900">{t('The ten-second microphone window was missed. Moving to the next question.')}</div>}
            {phase === 'transition' && <div className="mt-5 inline-flex items-center gap-2 text-gray-600"><ArrowPathIcon className="h-5 w-5 animate-spin" />{t('Moving to the next question...')}</div>}
            {phase === 'generating_followup' && <div className="mt-5 rounded border border-violet-200 bg-violet-50 p-4"><div className="inline-flex items-center gap-2 font-semibold text-violet-900"><ArrowPathIcon className="h-5 w-5 animate-spin" />{t('The examiner is preparing a follow-up from your answer...')}</div><p className="mt-2 text-sm text-violet-800">{t('Your answer segment is used only to generate this live question; the segment is not stored separately.')}</p></div>}
            {phase === 'followup_error' && <div className="mt-5 rounded border border-rose-200 bg-rose-50 p-4"><div className="font-semibold text-rose-900">{t('The live follow-up could not be prepared.')}</div><div className="mt-4 flex flex-wrap gap-3"><button type="button" onClick={retryFollowup} className="rounded bg-violet-700 px-4 py-2 font-semibold text-white">{t('Retry follow-up')}</button><button type="button" onClick={finishRecording} className="rounded border border-rose-300 bg-white px-4 py-2 font-semibold text-rose-800">{t('Finish simulation now')}</button></div></div>}
            {phase === 'mic_error' && <button type="button" onClick={openMicWindow} className="mt-5 rounded bg-violet-700 px-4 py-2 font-semibold text-white">{t('Try microphone again')}</button>}
          </div>
        )}

        {mixingNotice && <div className="mt-5 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">{mixingNotice}</div>}
        {phase === 'submitting' && <div className="mt-6 rounded border border-indigo-200 bg-indigo-50 p-5"><div className="flex items-center gap-2 font-semibold text-indigo-900"><ArrowPathIcon className="h-5 w-5 animate-spin" />{t('Uploading recording...')}</div><div className="mt-3 h-2 overflow-hidden bg-white"><div className="h-full bg-indigo-600" style={{ width: `${Math.max(5, progress || 0)}%` }} /></div><p className="mt-2 text-sm text-indigo-800">{progress >= 67 ? t('Recording uploaded. AI scoring is in progress.') : t('Keep this page open while the attempt is submitted.')}</p></div>}
        {['submit_error', 'empty'].includes(phase) && <button type="button" onClick={() => window.location.reload()} className="mt-5 rounded bg-violet-700 px-4 py-2 font-semibold text-white">{t('Restart simulation')}</button>}
        {phase === 'submit_error' && recordingBlob && <button type="button" onClick={() => submitBlob(recordingBlob)} className="ml-3 mt-5 rounded border border-violet-300 px-4 py-2 font-semibold text-violet-800">{t('Retry scoring')}</button>}
        {error && <div className="mt-5 rounded border border-rose-200 bg-rose-50 px-4 py-3 text-rose-900" role="alert">{error}</div>}
      </section>
      {phase !== 'intro' && <div className="mt-4 flex items-center gap-2 text-sm text-gray-500"><ClockIcon className="h-4 w-4" />{t('Strict timing is active for this simulation.')}</div>}
    </main>
  );
}
